/**
 * Debounce - group a series of sequential calls to a function
 * into a single call to that function after it stops getting
 * called for `wait` amount of ms.
 * @param {Function} fn
 * @param {Number} wait
 */
const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};


/**
 * Body Scroll Lock global object.
 * Use this to ensure body scroll is locked across browsers and OS.
 * More info: https://github.com/willmcpo/body-scroll-lock#usage-examples
 */
const bodyScroll = {
  lock(container) {
    bodyScrollLock.disableBodyScroll(container);
  },
  unlock(container) {
    bodyScrollLock.enableBodyScroll(container);
  },
  clear() {
    bodyScrollLock.clearAllBodyScrollLocks();
  },
}

/**
 * Menu Drawer Custom element class.
 */
class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    this.overlay = this.querySelector('[data-overlay]');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this)),
    );

    this.overlay?.addEventListener('click', this.closeMenuDrawer.bind(this, this.mainDetailsToggle.querySelector('summary')));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(summaryElement)
        : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    this.mainDetailsToggle.setAttribute('open', true);

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    bodyScroll.lock(summaryElement.nextElementSibling);
  }

  closeMenuDrawer(elementToFocus = false) {
    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle
      .querySelectorAll('.submenu-open')
      .forEach((submenu) => {
        submenu.classList.remove('submenu-open');
      });
    bodyScroll.unlock(
      this.mainDetailsToggle.querySelector('summary').nextElementSibling,
    );
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute('open') &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement
      .querySelector('summary')
      .setAttribute('aria-expanded', false);
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

/**
 * Header drawer custom element class.
 */
class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    
    this.debouncedResize = debounce(() => {
      if (
        !window.matchMedia(
          `(min-width: ${this.getAttribute('data-breakpoint')}px)`,
        ).matches
      )
        return;

      this.closeMenuDrawer();
    }, 300);
    window.addEventListener('resize', this.debouncedResize.bind(this));
  }

  openMenuDrawer(summaryElement) {
    super.openMenuDrawer(summaryElement);
  }

  closeMenuDrawer(elementToFocus) {
    super.closeMenuDrawer(elementToFocus);
  }
}

customElements.define('header-drawer', HeaderDrawer);

/**
 * Navigation opener custom element class.
 */
class NavigationOpener extends HTMLElement {
  constructor() {
    super();
    
    this.button = this.querySelector('button');
    this.mobileNavigation = document.querySelector('header-drawer');

    if (!this.mobileNavigation) {
      return;
    }

    this.button?.addEventListener('click', () => {
      this.mobileNavigation.openMenuDrawer(this.mobileNavigation.querySelector('summary'));
    })
  }
}

customElements.define('navigation-opener', NavigationOpener);

/**
 * Modal opener custom element class.
 * Used in combination with ModalDialog for inserting a dialog
 * trigger in the document.
 * `data-modal` property connects the modal with the modal-opener.
 * Must contain a button element.
 */
class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button, a');

    if (!button) return;

    button.addEventListener('click', (event) => {
      const modal = document.querySelector(this.getAttribute('data-modal'));

      if (modal) modal.show(button);

      event.preventDefault();
    });
  }
}
customElements.define('modal-opener', ModalOpener);

/**
 * Modal dialog custom element class.
 * Inserts a modal that is connected to a modal-opener.
 */
class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.dialogHolder = this.querySelector('[role="dialog"]');
    this.querySelectorAll('[id^="modalClose-"]').forEach(closeButton => closeButton.addEventListener(
      'click',
      this.hide.bind(this, false),
    ));
    this.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'ESCAPE') this.hide();
    });
    this.addEventListener('click', (event) => {
      if (event.target === this) this.hide();
    });
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    bodyScroll.lock(this.dialogHolder);
    this.setAttribute('open', '');
  }

  hide() {
    bodyScroll.unlock(this.dialogHolder);
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
  }
}
customElements.define('modal-dialog', ModalDialog);


const initMegamenu = () => {
  const header = document.querySelector('.site-header');

  if (!header) {
    return;
  }

  const navDropdowns = header.querySelectorAll('.site-nav summary.site-nav__link');

  navDropdowns.forEach(navDropdown => {
    ['mouseenter', 'focusin'].forEach(event => {
      navDropdown.addEventListener(event, () => {
        updateMegamenuVariable(navDropdown);
        hideSearchModal();
      });
    });

    navDropdown.nextElementSibling.querySelectorAll('details').forEach(subdetails => {
      subdetails.addEventListener('toggle', () => {
        updateMegamenuVariable(navDropdown);
      });
    })
  });

  function updateMegamenuVariable(navDropdown) {
    const dropdown = navDropdown.closest('details').querySelector('.site-nav__dropdown');

    if (!dropdown) {
      return;
    }

    const megamenuHeight = header.offsetHeight + dropdown.offsetHeight; 

    header.style.setProperty(
      '--megamenu-height',
      `${megamenuHeight}px`,
    );
  }

  function hideSearchModal() {
    const searchModal = document.querySelector('#SearchModal');

    if (!searchModal || !searchModal.classList.contains('modal--is-active')) {
      return;
    }

    searchModal.classList.add('modal--is-closing');
  }
}

initMegamenu();

document.addEventListener('modalOpen.SearchModal', () => {
  const results = document.querySelector('#SearchModal #predictive-search');

  if (!results) {
    return;
  }

  bodyScroll.lock(results);
})


document.addEventListener('modalClose.SearchModal', () => {
  const results = document.querySelector('#SearchModal #predictive-search');

  if (!results) {
    return;
  }

  bodyScroll.unlock(results);
})

// Update header cart icon
document.addEventListener('cart:updated', function(event) {
  const cartBubble = document.querySelector('.cart-link__bubble-num');

  if (cartBubble) {
    const cartItemsCount = event.detail.items.reduce((accumulator, currentValue) => accumulator + currentValue.quantity, 0);

    cartBubble.textContent = cartItemsCount;
  }

  document.body.classList.toggle('cart-has-items', event.detail.items.length > 0)
});